import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AgmMap } from '@agm/core';
import { FormBuilder, FormGroup, AbstractControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BOKA } from '../../../../classes/number';
import { Coordinates } from '../../../../classes/Coordinates';
import { FormValidation } from '../../../../classes/formValidation';
import { MapControlDirective } from '../../../../directives/map-control.directive';
import { IAgmMapClick, ICoordinates } from '../../../shared/interfaces';
import { eWeatherAPIs } from '../../../../interfaces/constants';
import { RanchBasicLocationService, RanchBasicMapParams } from './ranchBasicLocationService';
import { UpdateService } from '../../../../services/update.service';
import { RanchService } from '../../service';

@Component({
	moduleId: module.id,
	selector: 'ranch-basic-form',
	templateUrl: 'main.html',
	styleUrls: ['main.scss']
})

export class RanchBasicFormComponent implements OnInit, OnDestroy {
	@ViewChild(AgmMap, { static: false }) private map: AgmMap;
	@ViewChild(MapControlDirective, { static: false }) private mapControl: MapControlDirective;

	public coordinateExplanation: string;

	@Input()
	public form: FormGroup;

	@Input()
	public inEditMode: boolean;

	public loadingData = true;

	public markers: ICoordinates[] = new Array();

	@Input()
	public ranchOwnerFullName: string;

	@Output()
	public formReady: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

	private _newRanchMapIsReady = false;
	private _newRanchMapParams: RanchBasicMapParams;
	private _isNewRanchMarkerPlacementRequired = false;
	private _subscriptions$: Subject<boolean>;

	constructor(
		private _fb: FormBuilder,
		private _ranchService: RanchService,
		private _updateService: UpdateService
	) { }

	public get f(): { [key: string]: AbstractControl } {
		if (!this.form) {
			return null;
		} else {
			return this.form.controls;
		}
	}

	ngOnInit(): void {
		this._subscriptions$ = new Subject();
		this._initializeForm();
		this._bindCoordinatesValueChanges();

		if (this.inEditMode) {
			this.form.markAsDirty();
			this._getEditRanchData();
		} else {
			this.loadingData = false;
			this._resetCreateRanchMap();
		}
	}

	ngOnDestroy(): void {
		if (!this._subscriptions$) {
			return;
		}

		this._subscriptions$.next(true);
		this._subscriptions$.complete();
	}

	public changeCoordinatesMap(event: IAgmMapClick): void {
		let lat = BOKA.NumberUtil.truncateDecimal(event.coords.lat);
		let lng = BOKA.NumberUtil.truncateDecimal(event.coords.lng);

		this.form.get('Coordinates').setValue(lat + ',' + lng);
		this.form.get('Coordinates').markAsTouched();
		this._placeMarker(lat, lng);
	}

	public mapReady(): void {
		this._newRanchMapIsReady = true;
		this._resetNewRanchMapIfReady(this._isNewRanchMarkerPlacementRequired);
	}

	public resetMap(lat: number, lng: number, zoom: number, isMarkerPlacementRequired = false): void {
		if (!this.map || !this.mapControl || !lat || !lng) {
			return;
		}

		setTimeout(() => {
			if (isMarkerPlacementRequired) {
				this._placeMarker(lat, lng);
			}

			this.map.triggerResize().then(() => {
				this.mapControl.centerMap(lat, lng);
				this.mapControl.setZoom(zoom);
			});
		}, 200);
	}

	private _bindCoordinatesValueChanges(): void {
		this.form.get('Coordinates').valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			let coordinates: Coordinates;

			if (!this.form.get('Coordinates').valid) {
				return;
			}

			coordinates = new Coordinates(this.form.get('Coordinates').value);

			if (!coordinates) {
				throw new Error('invalid coordinates');
			}

			RanchBasicLocationService.getWeatherSettingsFromCoordinates(coordinates.lat, coordinates.lng).then((resp) => {
				this.form.get('SpatialETAvailable').setValue(resp.SpatialETAvailable);
				this.form.get('WeatherAPI_Id').setValue(resp.WeatherAPI_Id);

				if (resp.WeatherAPI_Id === eWeatherAPIs.Invalid) {
					this.coordinateExplanation = 'There is no weather data available for the location you selected.';
				} else {
					this.coordinateExplanation = null;
				}
			});
		});
	}

	private _getEditRanchData(): void {
		this._ranchService.getRanchBasicInfo()
			.then((res) => {
				let coords: Coordinates;

				if (!res) {
					return;
				}

				if (!this.form) {
					throw new Error('form is empty');
				}

				coords = new Coordinates(res.Coordinates);
				this.form.get('Name').setValue(res.RanchName);
				this.form.get('Coordinates').setValue(coords.toString());
				this.form.get('Acres').setValue(res.Acres);
				this.form.get('SpatialETAvailable').setValue(res.SpatialETAvailable);
				this.form.get('WeatherAPI_Id').setValue(res.WeatherAPI_Id);

				this.ranchOwnerFullName = res.RanchOwnerFullName;
				this.loadingData = false;
				this._resetExistingRanchMap();
			});
	}

	private _initializeForm(): void {
		let value = this.form ? this.form.value : null;

		this.form = this._fb.group({
			Coordinates: [null, { validators: [Validators.required,
				FormValidation.CoordinateStringIsValid()], updateOn: 'blur'}],
			Name: [null, Validators.required],
			Acres: [null, [Validators.required, Validators.min(0.000001), Validators.max(2147483647)]],
			SpatialETAvailable: [null, [Validators.required]],
			WeatherAPI_Id: [null, [Validators.required]],
		});

		if (value) {
			this.form.patchValue(value);
		}

		this.formReady.emit(this.form);
	}

	private _placeMarker(lat: number, lng: number): void {
		this.markers = new Array();

		this.markers.push({
			lat: lat,
			lng: lng
		});
	}

	private _resetExistingRanchMap(): void {
		setTimeout(() => {
			let coords = new Coordinates(this.form.get('Coordinates').value);

			if (coords === null) {
				return;
			}

			this.resetMap(coords.lat, coords.lng, 10, true);
		}, 0);
	}

	private _resetCreateRanchMap(): void {
		if (this.form.get('Coordinates').valid && this.form.get('Coordinates').value) {
			let coords = new Coordinates(this.form.get('Coordinates').value);

			if (coords !== null) {
				this._newRanchMapParams = {
					Lat: coords.lat,
					Lng: coords.lng,
					Zoom: 10
				};
				this._isNewRanchMarkerPlacementRequired = true;
				this._resetNewRanchMapIfReady(true);
				return;
			}
		}

		this._resetNewRanchMap();
	}

	private _resetNewRanchMap(): void {
		RanchBasicLocationService.getNewRanchMapParams(this._updateService).then((mapParams) => {
			this._newRanchMapParams = mapParams;
			this._resetNewRanchMapIfReady();
		});
	}

	private _resetNewRanchMapIfReady(isMarkerPlacementRequired = false): void {
		if (!this._newRanchMapIsReady || !this._newRanchMapParams) {
			return;
		}

		this.resetMap(
			this._newRanchMapParams.Lat,
			this._newRanchMapParams.Lng,
			this._newRanchMapParams.Zoom,
			isMarkerPlacementRequired
		);
	}
}
